import * as React from "react";
import { Plan, PricePlanTier, WebSettings } from "@/interfaces";
import { z } from "zod";
import { CoachService } from "@/services";
import { PlayersServices as BedrockPlayersServices, CreatePlayerParams } from "@/services/bedrock";
import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";
import {
  URLS,
  useAccountSettings,
  usePlayerFetch,
  usePlayerSearch,
  useSegmentation,
  useSocialCoachSession,
} from "@/utils";
import { Button, Field, Input, Textarea, RadioGroup, Label, Switch } from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { SegmentationDropDown } from "@/containers/Coach/EditPlayer/components/SegmentationDropdown";
import { PageWrapper } from "@/components/PageWrapper";
import { RouteComponentProps, withRouter } from "react-router";
import { capitalize } from "lodash";
import { cn } from "@/lib/utils";

type Props = RouteComponentProps;

const segmentationArray = (label: string) =>
  z.array(z.string().max(128, `${label} must be less than 128 characters`)).optional();

const formSchema = z.object({
  subscriptionTier: z.enum(PricePlanTier),
  firstName: z.string().min(1, "First name is required").max(255, "First name must be less than 255 characters"),
  lastName: z.string().min(1, "Last name is required").max(255, "Last name must be less than 255 characters"),
  email: z
    .email("Invalid email address")
    .min(1, "Email is required")
    .max(255, "Email must be less than 255 characters"),
  complianceDisclaimer: z.string().max(255, "Compliance disclaimer must be less than 255 characters").optional(),
  internalIdentifier: z.string().max(255, "Internal identifier must be less than 255 characters").optional(),
  title: z.string().max(255, "Title must be less than 255 characters").optional(),
  phone: z.string().max(255, "Phone must be less than 255 characters").optional(),
  skipOnboardingEmail: z.boolean().default(false).optional(),
  branchNames: segmentationArray("Branch name"),
  regionNames: segmentationArray("Region name"),
  stateCodes: segmentationArray("State Code"),
  costCenterNames: segmentationArray("Cost center name"),
  industryRoleNames: segmentationArray("Industry role name"),
});

export function AddPlayerFC({ history }: Props) {
  const { coachId } = useSocialCoachSession();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [plans, setPlans] = React.useState<Plan[]>([]);

  const { hasAccountSetting } = useAccountSettings();
  const hasCostCenter = hasAccountSetting(WebSettings.COST_CENTER);
  const hasPlusTier = hasAccountSetting(WebSettings.SC_PLUS_TIER);

  const { clearPlayerSearchCache } = usePlayerSearch();
  const { clearPlayerFetchCache } = usePlayerFetch();
  const { clearSegmentationCache } = useSegmentation();

  const defaultValues: CreatePlayerParams = {
    subscriptionTier: PricePlanTier.PLAYER_BASIC,
    firstName: "",
    lastName: "",
    email: "",
    complianceDisclaimer: "",
    internalIdentifier: "",
    title: "",
    phone: "",
    branchNames: [],
    regionNames: [],
    stateCodes: [],
    costCenterNames: [],
    industryRoleNames: [],
    skipOnboardingEmail: false,
  };

  const form = useForm<CreatePlayerParams>({
    resolver: zodResolver(formSchema),
    mode: "onTouched",
    defaultValues,
  });

  React.useEffect(() => {
    setLoading(true);
    if (coachId) {
      CoachService.getPlans(coachId!)
        .then(fetchedPlans => {
          setPlans(fetchedPlans);
        })
        .catch(() => {
          toast.error("Your account doesn't have a SocialCoach plan, please contact support.");
        })
        .finally(() => setLoading(false));
    }
  }, [coachId]);

  React.useEffect(() => {
    if (plans.length > 0) {
      const targetTier = hasPlusTier ? PricePlanTier.PLAYER_PLUS : PricePlanTier.PLAYER_BASIC;
      const hasTargetTier = plans.some(p => p.tier === targetTier);
      form.setValue("subscriptionTier", hasTargetTier ? targetTier : plans[0]?.tier || PricePlanTier.PLAYER_BASIC);
    }
  }, [plans, form, hasPlusTier]);

  const onSubmit = (data: CreatePlayerParams) => {
    toast.info("Preparing player subscription...");
    const selectedPlan = plans.find(p => p.id && p.tier === data.subscriptionTier);
    if (!selectedPlan?.id) {
      toast.error("The selected plan could not be found. Please try again.");
      return;
    }

    setLoading(true);
    BedrockPlayersServices.createPlayer(data)
      .then(() => {
        clearPlayerSearchCache();
        clearPlayerFetchCache();
        clearSegmentationCache();
        toast.success("Player added successfully");
        history.push(URLS.coach.players);
      })
      .catch(e => {
        toast.error(e);
        setLoading(false);
      });
  };

  return (
    <PageWrapper title={"New Player"}>
      <form id="new-player-form" onSubmit={form.handleSubmit(onSubmit)} className="w-full lg:max-w-2xl">
        <fieldset disabled={loading} className="contents">
          <Field.Set>
            <div className="text-sm font-bold uppercase tracking-wider text-slate-500/80">Profile Information</div>
            <Field.Group>
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name="firstName"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label htmlFor="new-player-first-name">First Name</Field.Label>
                      <Input
                        {...field}
                        id="new-player-first-name"
                        aria-invalid={fieldState.invalid}
                        placeholder="First Name"
                        autoComplete="one-time-code"
                      />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="lastName"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label htmlFor="new-player-last-name">Last Name</Field.Label>
                      <Input
                        {...field}
                        id="new-player-last-name"
                        aria-invalid={fieldState.invalid}
                        placeholder="Last Name"
                        autoComplete="one-time-code"
                      />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="email"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label htmlFor="new-player-email">Email</Field.Label>
                      <Input
                        {...field}
                        id="new-player-email"
                        aria-invalid={fieldState.invalid}
                        placeholder="Email"
                        autoComplete="one-time-code"
                      />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="phone"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label htmlFor="new-player-phone">Phone</Field.Label>
                      <Input
                        {...field}
                        id="new-player-phone"
                        aria-invalid={fieldState.invalid}
                        placeholder="Phone"
                        autoComplete="one-time-code"
                      />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="title"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label htmlFor="new-player-title">Title</Field.Label>
                      <Input
                        {...field}
                        id="new-player-title"
                        aria-invalid={fieldState.invalid}
                        placeholder="Title"
                        autoComplete="one-time-code"
                      />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="internalIdentifier"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label htmlFor="new-player-internal-id">Internal Identifier</Field.Label>
                      <Input
                        {...field}
                        id="new-player-internal-id"
                        aria-invalid={fieldState.invalid}
                        placeholder="For your own reporting purposes"
                        autoComplete="one-time-code"
                      />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <div className="col-span-2">
                  <Controller
                    name="complianceDisclaimer"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <Field.Label htmlFor="new-player-disclaimer">Compliance Disclaimer</Field.Label>
                        <Textarea
                          {...field}
                          id="new-player-disclaimer"
                          aria-invalid={fieldState.invalid}
                          placeholder="Example: NMLS# 12345"
                          autoComplete="one-time-code"
                        />
                        {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                      </Field>
                    )}
                  />
                </div>
                <Controller
                  name="subscriptionTier"
                  control={form.control}
                  render={() => (
                    <Field>
                      <Field.Label>Plan Tier</Field.Label>
                      <RadioGroup
                        value={form.getValues("subscriptionTier")}
                        onValueChange={v => form.setValue("subscriptionTier", v)}
                        className="flex items-center gap-6 mt-1"
                        disabled={loading}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroup.Item value={PricePlanTier.PLAYER_BASIC} id="tier-basic" />
                          <Label htmlFor="tier-basic" className="font-medium cursor-pointer">
                            SocialCoach Basic
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroup.Item
                            value={PricePlanTier.PLAYER_PLUS}
                            id="tier-plus"
                            disabled={!hasPlusTier || !plans.some(p => p.tier === PricePlanTier.PLAYER_PLUS)}
                          />
                          <Label
                            htmlFor="tier-plus"
                            className={cn(
                              "font-medium cursor-pointer",
                              (!hasPlusTier || !plans.some(p => p.tier === PricePlanTier.PLAYER_PLUS)) && "opacity-50"
                            )}
                          >
                            SocialCoach+
                          </Label>
                        </div>
                      </RadioGroup>
                    </Field>
                  )}
                />
                <Controller
                  name="skipOnboardingEmail"
                  control={form.control}
                  render={({ field }) => (
                    <Field>
                      <Field.Label>Send Welcome Email</Field.Label>
                      <Switch
                        id="send-welcome-email"
                        checked={!field.value}
                        onCheckedChange={v => field.onChange(!v)}
                      />
                    </Field>
                  )}
                />
              </div>
            </Field.Group>
          </Field.Set>
          <div className="my-10"> </div>
          <Field.Set>
            <div>
              <div className="text-sm font-bold uppercase tracking-wider text-slate-500/80">Segmentation</div>
              <div className="mt-1 text-sm text-slate-500">
                Group your team to automate relevant prompts and campaigns
              </div>
            </div>

            <Field.Group>
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name="branchNames"
                  control={form.control}
                  render={({ field }) => (
                    <SegmentationDropDown
                      id="branchNames"
                      type="BRANCH"
                      label="Branch"
                      fieldValue={field.value}
                      handleChange={data => field.onChange(data.value)}
                      disabled={loading}
                      multiple={true}
                      allowAdditions={true}
                    />
                  )}
                />
                <Controller
                  name="regionNames"
                  control={form.control}
                  render={({ field }) => (
                    <SegmentationDropDown
                      id="regionNames"
                      type="REGION"
                      label="Region"
                      fieldValue={field.value}
                      handleChange={data => field.onChange(data.value)}
                      disabled={loading}
                      multiple={true}
                      allowAdditions={true}
                    />
                  )}
                />
                <Controller
                  name="stateCodes"
                  control={form.control}
                  render={({ field }) => (
                    <SegmentationDropDown
                      id="stateCodes"
                      type="STATE"
                      label="States"
                      fieldValue={field.value}
                      handleChange={data => field.onChange(data.value)}
                      disabled={loading}
                      multiple={true}
                      allowAdditions={false}
                    />
                  )}
                />
                {hasCostCenter && (
                  <Controller
                    name="costCenterNames"
                    control={form.control}
                    render={({ field }) => (
                      <SegmentationDropDown
                        id="costCenterNames"
                        type="COST_CENTER"
                        label="Cost Center"
                        fieldValue={field.value}
                        handleChange={data => field.onChange(data.value)}
                        disabled={loading}
                        multiple={true}
                        allowAdditions={true}
                      />
                    )}
                  />
                )}
                <Controller
                  name="industryRoleNames"
                  control={form.control}
                  render={({ field }) => (
                    <SegmentationDropDown
                      id="industryRoleNames"
                      type="INDUSTRY_ROLE"
                      label="Industry Role"
                      fieldValue={field.value}
                      handleChange={data => field.onChange(data.value)}
                      disabled={loading}
                      multiple={true}
                      allowAdditions={false}
                    />
                  )}
                />
              </div>
            </Field.Group>
          </Field.Set>

          <Button
            type="submit"
            form="new-player-form"
            className="mt-6 w-full lg:w-auto"
            loading={loading}
            disabled={loading}
          >
            Add Player
          </Button>
        </fieldset>
      </form>
      {Object.keys(form.formState.errors).length > 0 && (
        <div className="mt-4 p-4 bg-destructive/10 text-destructive rounded-lg text-sm">
          <p className="font-bold mb-1">Please fix the following errors:</p>
          <ul className="list-disc list-inside">
            {Object.entries(form.formState.errors).map(([key, error]) => (
              <li key={key}>
                {capitalize(key)}: {error?.message as string}
              </li>
            ))}
          </ul>
        </div>
      )}
    </PageWrapper>
  );
}

export const AddPlayer = withRouter(AddPlayerFC);
